/**
 * Tenant-tier session storage (W79 PR B / W65-F1).
 *
 * Tenant users sign in via `POST /api/v1/tenant/sign-in`, which mints an
 * HS256 JWT with `senviro_tier=tenant`. The token is unrelated to the
 * partner-tier MSAL session; it lives entirely in localStorage with a
 * scoped key so a partner user can stay signed in alongside the tenant
 * user on the same browser without one clobbering the other.
 *
 * `<PLACEHOLDER: confirm with user — localStorage is the v1 choice
 * (matches partner MSAL cache behaviour). If the team prefers an
 * httpOnly cookie issued by the API, swap this module + the
 * apiFetch getter behind a small adapter and the call-sites stay
 * unchanged.>`
 */

const STORAGE_KEY = 'senviro.tenant.session.v1';

export interface TenantSession {
  token: string;
  /** ISO-8601. Mirrors what the BE returned from `/tenant/sign-in`. */
  expiresAt: string;
  organizationId: string;
  email: string;
  /** Populated after the first `/tenant/me` round-trip. */
  tenantUserId?: string;
  fullName?: string;
}

export function readTenantSession(): TenantSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TenantSession;
    if (!parsed.token || !parsed.expiresAt) return null;
    if (new Date(parsed.expiresAt).getTime() < Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeTenantSession(session: TenantSession): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  window.dispatchEvent(new CustomEvent('senviro:tenant-session-changed'));
}

export function clearTenantSession(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('senviro:tenant-session-changed'));
}

/**
 * Per-tenant-user lease-selection persistence. Used by F6-S2 +
 * downstream features so a returning tenant lands on the same building
 * they last interacted with.
 */
const SELECTED_LEASE_KEY = 'senviro.tenant.selectedLease.v1';

export function readSelectedLeaseId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(SELECTED_LEASE_KEY);
}

export function writeSelectedLeaseId(leaseId: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SELECTED_LEASE_KEY, leaseId);
  window.dispatchEvent(new CustomEvent('senviro:tenant-lease-changed'));
}
